// Kitchen-specific stroke icons in the same hand-drawn 24px style as
// src/ui/Icon.tsx (kept here so the kitchen feature is self-contained).

const PATHS = {
  plate: "M12 17.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11ZM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM2.5 4v3.5a1.5 1.5 0 0 0 3 0V4M4 9v11M20.5 20V4c-1.6 1-2.5 3.3-2.5 7h2.5",
  cart: "M3 4h2l2.4 10.5a1.5 1.5 0 0 0 1.5 1.1h8.4a1.5 1.5 0 0 0 1.5-1.1L21 8H6.2M9.5 20h.01M17.5 20h.01",
  timer: "M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM12 9v4l2.5 1.5M10 2.5h4M18.5 5.5l1.5-1.5",
  speaker: "M4 9h3l5-4v14l-5-4H4zM16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11",
  mute: "M4 9h3l5-4v14l-5-4H4zM16 9.5l5 5M21 9.5l-5 5",
  swap: "M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7",
  external: "M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  star: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5Z",
  scale: "M5 20h14l-1.6-11H6.6L5 20ZM9 9a3 3 0 0 1 6 0M12 12.5v3",
  backspace: "M9 5h11v14H9l-6-7 6-7ZM12.5 9.5l5 5M17.5 9.5l-5 5",
  bell: "M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16ZM10 20.5a2 2 0 0 0 4 0",
  minus: "M5 12h14",
} as const;

export type KitchenGlyphName = keyof typeof PATHS;

export function KGlyph({ name, size = "1.35rem", className, title }: { name: KitchenGlyphName; size?: number | string; className?: string; title?: string }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
    >
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  );
}

// Foods and household things for the shopping board's shortcuts (shortcuts.ts).
// Same 24px stroke style; several items share a glyph (a jar, a can, a sack).
const FOOD = {
  bag: "M5 8h14l-1 13H6L5 8ZM9 8V6a3 3 0 0 1 6 0v2",
  // produce
  apple: "M12 7.5c-1.5-1-5.5-1.3-6.5 2.5-.9 3.6 1.4 9.5 4.3 9.5.9 0 1.4-.5 2.2-.5s1.3.5 2.2.5c2.9 0 5.2-5.9 4.3-9.5-1-3.8-5-3.5-6.5-2.5ZM12 7.5c0-2 .8-3.5 2.5-4.5",
  banana: "M5 6.5c-.5 7.5 5.5 13 13.5 11.5.8-.2.9-1 .2-1.3C12.5 15 9 11 8 6.5H5ZM6.5 6.5L6 4.5",
  orange: "M12 20.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15ZM12 5.5c.5-1.8 2-2.8 4-2.8-.3 1.8-1.8 2.8-4 2.8ZM9.5 12h.01M14 10.5h.01M13.5 15h.01",
  lemon: "M5.5 17.5c-2.5-3-1.5-8 2-10.5s8-3 10.5.5c1 .1 1.6.8 1.5 1.8 1.5 3-.5 7.5-4 9.5s-7.5 1.5-8.5-.4c-1-.1-1.6-.4-1.5-.9Z",
  grapes: "M6 9a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM10 9a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM14 9a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM8 12.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM12 12.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM10 16a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM12 7V3.5M12 5c1.5-1.5 3.5-1.5 5 0",
  strawberry: "M12 20c-4 0-7-6-7-9.5C5 8 7 7 12 7s7 1 7 3.5C19 14 16 20 12 20ZM8 4.5l4 2.5 4-2.5M12 7V3M9.5 11h.01M14.5 11h.01M12 14h.01M10 16.5h.01M14 16.5h.01",
  avocado: "M12 21a5.5 5.5 0 0 0 5.5-5.5c0-3-1.5-4.5-2.5-7.5C14.3 5.5 13.5 3 12 3S9.7 5.5 9 8c-1 3-2.5 4.5-2.5 7.5A5.5 5.5 0 0 0 12 21ZM12 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z",
  tomato: "M12 20a7.5 7 0 0 0 7.5-7c0-3.8-3.3-6-7.5-6s-7.5 2.2-7.5 6a7.5 7 0 0 0 7.5 7ZM8.5 6.5L12 8.5l3.5-2M12 8.5V4.5M10 8l-2.5 1M14 8l2.5 1",
  lettuce: "M5 13a7 7 0 0 0 14 0M5 13c-1-2 0-4 2-4.5C7 6 9.5 4.5 12 5.5c2.5-1 5 .5 5 3 2 .5 3 2.5 2 4.5M12 20v-8M12 12l-3-3M12 14l3-3",
  carrot: "M15.5 8.5L4 20l3-8.5c.8-2.3 2.6-3.6 4.5-4 1.7-.4 3 .2 4 1ZM15.5 8.5L19 5M15 7l.5-4M17 9l4-.5M9 13l1.5 1.5M7 16l1 1",
  potato: "M6 8.5c2-3 8-4 11-1.5s2.5 8-1 10.5-9.5 2.5-11-.5S4 11.5 6 8.5ZM9 10h.01M14 9.5h.01M15 14h.01M10 15h.01",
  onion: "M12 3.5c0 2-1 3-2.5 4.2C7 9.5 5.5 11.5 5.5 14a6.5 6 0 0 0 13 0c0-2.5-1.5-4.5-4-6.3C13 6.5 12 5.5 12 3.5ZM12 20c-2-1.5-3-3.5-3-6s1-4.5 3-6.5M12 20c2-1.5 3-3.5 3-6s-1-4.5-3-6.5",
  garlic: "M12 3c0 2.5-1 3.5-3 5S5 11.5 5 14.5C5 18 8 20.5 12 20.5s7-2.5 7-6c0-3-2-4.5-4-6.5S12 5.5 12 3ZM12 9c-1.5 2.5-2 5-1.5 11.5M12 9c1.5 2.5 2 5 1.5 11.5",
  broccoli: "M10 21l.5-6.5M14 21l-.5-6.5M10 21h4M8 14.5a3 3 0 0 1-1.5-5.6 3.5 3.5 0 0 1 5.5-3.4 3.5 3.5 0 0 1 5.5 3.4A3 3 0 0 1 16 14.5H8ZM12 14.5V11",
  pepper: "M12 7c-3-1.5-7 0-7 4.5 0 5 3 9 5.5 9 .7 0 1-.5 1.5-.5s.8.5 1.5.5c2.5 0 5.5-4 5.5-9C19 7 15 5.5 12 7ZM12 7V5c0-1 .8-2 2-2",
  cucumber: "M5 18.5c-2-2-.5-5.5 3-9s7-6 9-4 .5 5.5-3 9-7 6-9 4ZM9 12h.01M12 10h.01M11 14.5h.01M14 12h.01",
  mushroom: "M3.5 12a8.5 7 0 0 1 17 0H3.5ZM9.5 12v6.5a2.5 2.5 0 0 0 5 0V12M9 8h.01M14 7h.01",
  corn: "M12 21c-2.5 0-4-4-4-9s1.5-9 4-9 4 4 4 9-1.5 9-4 9ZM8.5 9h7M8 13h8M8.5 17h7M12 3v18M4.5 12c1 4 3.5 8 7.5 9M19.5 12c-1 4-3.5 8-7.5 9",
  // dairy and eggs
  milk: "M7 9l2-3h6l2 3v12H7V9ZM9 6V3h6v3M7 9h10M10 13.5h4v3h-4z",
  egg: "M12 20.5c-3.6 0-6-2.6-6-6.3C6 9.5 9 3.5 12 3.5s6 6 6 10.7c0 3.7-2.4 6.3-6 6.3Z",
  butter: "M3 15l5-5h13l-5 5H3ZM3 15v3h13v-3M16 18l5-5v-3M7 12.5h4",
  cheese: "M3 18.5V15l11-8 7 4v7.5H3ZM3 15h18M8 17a1 1 0 1 0 2 0 1 1 0 1 0-2 0ZM15 17.5h.01M13 12h.01",
  yogurt: "M5 7h14l-1.5 13h-11L5 7ZM4 4.5h16V7H4zM8.5 12.5c1-1 2-1 3 0s2 1 3 0",
  tub: "M4 9h16l-1 10H5L4 9ZM3.5 6h17v3h-17zM9 14h6",
  // meat and fish
  drumstick: "M14 14c3 0 6-2.5 6-6a4 4 0 0 0-4-4c-3.5 0-6 3-6 6 0 1 .2 1.7.5 2.2L7 15.7a1.8 1.8 0 1 0-2.4 2.7 1.8 1.8 0 1 0 2.7-2.4l3.5-3.5c.5.3 1.2.5 2.2.5",
  steak: "M5 9c2-4 9-5.5 13-3s3 8-1 10.5-6 1.5-8 3.5-5.5.5-5.5-2.5S3.5 12 5 9ZM13.5 10.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0Z",
  bacon: "M3 8c2-1.5 4 1.5 6 0s4-1.5 6 0 4 1.5 6 0M3 16c2-1.5 4 1.5 6 0s4-1.5 6 0 4 1.5 6 0M3 8v8M21 8v8M3 12c2-1.5 4 1.5 6 0s4-1.5 6 0 4 1.5 6 0",
  sausage: "M4.5 15.5c-1.5-1.5-.5-4 2-4.5 3-.6 5-2 6.5-4.5s4-3 5.5-1.5.5 4-1 6c-2.5 3.5-6.5 5.5-10 5.5-1.3 0-2.2-.3-3-1ZM3.5 17l-1 1M19.5 5.5l1-1",
  fish: "M3 12c3-4.5 9-6 14-2l4-3v10l-4-3c-5 4-11 2.5-14-2ZM8 11h.01",
  shrimp: "M17 5c-5 0-10 3-10 8.5 0 3 2 5.5 5 5.5h2M17 5c2 0 3.5 1.5 3.5 3.5S19 12 17 12h-2c-2 0-3 1.5-3 3M12 19l-2 2.5h5L14 19M10 9l2 2.5M7.5 12.5l3 1M17 5l-3-2.5",
  deli: "M4 12a8 5 0 0 0 16 0 8 5 0 0 0-16 0ZM4 12v3a8 5 0 0 0 16 0v-3M9 11.5a3 1.8 0 0 0 6 0 3 1.8 0 0 0-6 0Z",
  // bakery
  bread: "M5 20v-9a3.5 3.5 0 0 1-1-6.5C5.5 3.5 8.5 3 12 3s6.5.5 8 1.5a3.5 3.5 0 0 1-1 6.5v9H5Z",
  bagel: "M12 19a8 6.5 0 1 0 0-13 8 6.5 0 0 0 0 13ZM12 14.5a3 2 0 1 0 0-4 3 2 0 0 0 0 4Z",
  tortilla: "M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8 9h.01M15 8h.01M10 15h.01M16 13h.01M12 12h.01",
  bun: "M4 13c0-4 3.5-7 8-7s8 3 8 7H4ZM4 13v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M9 9.5h.01M12 8.5h.01M15 9.5h.01",
  croissant: "M3 15c1-4 4-6 9-6s8 2 9 6c-1 1-2.5 1-3.5 0-.8-2.5-2-4-5.5-4s-4.7 1.5-5.5 4c-1 1-2.5 1-3.5 0ZM8.5 10.5l1 5M15.5 10.5l-1 5M12 9v6",
  muffin: "M6 12l1.5 8h9l1.5-8M5 12a2 2 0 0 1 .5-4A4 4 0 0 1 12 5a4 4 0 0 1 6.5 3 2 2 0 0 1 .5 4H5ZM10 12l.5 8M14 12l-.5 8",
  cake: "M4 20h16v-7H4v7ZM4 16c1.5 1 2.5 1 4 0s2.5-1 4 0 2.5 1 4 0 2.5-1 4 0M8 13V9.5M12 13V9.5M16 13V9.5M8 7h.01M12 7h.01M16 7h.01",
  // pantry
  sack: "M7 7c-2 3-3 7-2 11 .3 1.3 1.3 2 2.5 2h9c1.2 0 2.2-.7 2.5-2 1-4 0-8-2-11M7 7h10M8 4l1.5 3M16 4l-1.5 3M8 4h8M9.5 13h5",
  rice: "M3.5 11h17a8.5 8.5 0 0 1-17 0ZM7 11c0-3 2-5 5-5s5 2 5 5M10 8.5h.01M13 8h.01M12 10h.01",
  pasta: "M8 3c0 6 1 12 2 18M12 3v18M16 3c0 6-1 12-2 18M7 11h10",
  cereal: "M6 4h12v17H6zM6 8h12M9.5 13a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0ZM12.5 16a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0ZM9 17.5h.01",
  jar: "M7 8h10v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V8ZM6.5 4h11v4h-11zM7 12h10v5H7",
  can: "M6 5.5c0-1.4 2.7-2 6-2s6 .6 6 2v13c0 1.4-2.7 2-6 2s-6-.6-6-2v-13ZM6 5.5c0 1.4 2.7 2 6 2s6-.6 6-2M6 10h12M6 15h12",
  oil: "M10 3h4v3l2 3v12H8V9l2-3V3ZM8 13h8M10 6h4",
  coffee: "M5 9h11v7a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9ZM16 11h1.5a2.5 2.5 0 0 1 0 5H16M8 3.5c-.8 1 .8 2 0 3M11 3.5c-.8 1 .8 2 0 3M14 3.5c-.8 1 .8 2 0 3",
  tea: "M8 12l4-3 4 3v8H8v-8ZM12 9V3h4v2M10.5 15h3",
  salt: "M8 9c0-3 1.8-5 4-5s4 2 4 5M7 9h10l-1 12H8L7 9ZM10.5 6.5h.01M13.5 6.5h.01M12 5.5h.01",
  // frozen
  frozen: "M6 4h12l-1 4 1 13H6L7 8 6 4ZM7 8h10M12 11v7M9 12.5l6 4M15 12.5l-6 4",
  iceCream: "M8 11l4 10 4-10M7 11a5 5 0 1 1 10 0H7ZM9.5 14.5l4.5-3.5M10.5 17l3.5-3",
  pizza: "M3.5 6.5C9 3 15 3 20.5 6.5L12 21 3.5 6.5ZM5.5 9.5c4-2 9-2 13 0M10 12h.01M14 11.5h.01M12 15.5h.01",
  ice: "M5 8l7-4 7 4v8l-7 4-7-4V8ZM5 8l7 4 7-4M12 12v8",
  waffle: "M4 4h16v16H4zM4 9.3h16M4 14.7h16M9.3 4v16M14.7 4v16",
  fries: "M6 11l1.5 10h9L18 11H6ZM8 11l-.5-7M11 11V3M14 11l.5-7.5M16.5 11l1-5.5M9.5 16a2.5 2.5 0 0 0 5 0",
  // drinks
  water: "M10 2.5h4V5c2 1 2.5 2.5 2.5 4v11a1.5 1.5 0 0 1-1.5 1.5H9A1.5 1.5 0 0 1 7.5 20V9c0-1.5.5-3 2.5-4V2.5ZM7.5 11h9M7.5 16h9",
  juice: "M6 9h10v12H6zM6 9l1.5-3h7L16 9M13 6V3l3.5-1.5M9 14a2 2 0 1 0 4 0 2 2 0 1 0-4 0Z",
  beer: "M6 7h10v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7ZM16 9.5h1.5a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H16M6 7a2.5 2.5 0 0 1 3-3 3 3 0 0 1 4 0 2.5 2.5 0 0 1 3 3M9.5 11v6M12.5 11v6",
  wine: "M8 3h8l.5 5a4.5 4.5 0 0 1-9 0L8 3ZM12 12.5V20M8.5 20.5h7M7.8 6.5h8.4",
  glass: "M7 4h10l-1.5 16h-7L7 4ZM7.5 9h9M11 13h.01M13 16h.01M12.5 11.5h.01",
  // snacks
  chips: "M6 3.5c2 1 10 1 12 0l-1 2.5 1 12.5 1 2c-2.5-1-11.5-1-14 0l1-2 1-12.5-1-2.5ZM9.5 12a2.5 2 0 1 0 5 0 2.5 2 0 1 0-5 0Z",
  cookie: "M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM9 9h.01M14.5 8.5h.01M15 14h.01M9.5 14.5h.01M12 11.5h.01",
  chocolate: "M6 3h12v18H6zM6 9h12M6 15h12M12 3v18",
  popcorn: "M6 10l2 11h8l2-11H6ZM10 10l.5 11M14 10l-.5 11M6 10a2.5 2.5 0 0 1 1.5-4.5A3 3 0 0 1 12 4a3 3 0 0 1 4.5 1.5A2.5 2.5 0 0 1 18 10",
  cracker: "M5 5h14v14H5zM9 9h.01M15 9h.01M12 12h.01M9 15h.01M15 15h.01",
  nut: "M12 3c4 3 6 7 6 11a6 6 0 0 1-12 0c0-4 2-8 6-11ZM12 7c1.5 2 2 4 2 7",
  pretzel: "M8 19c-3-1-4.5-4-3.5-7.5S9 6 12 9.5 15 6 18.5 8.5 20 16 16 19M8 19l8-8M16 19l-8-8",
  bar: "M3 9.5l15-4 3 9-15 4-3-9ZM7 11l.5 1.5M11 10l.5 1.5M15 9l.5 1.5M8 15l.5 1.5M12 14l.5 1.5",
  // household and cleaning
  roll: "M4 9a3 5 0 1 0 6 0 3 5 0 1 0-6 0ZM7 4h11a3 5 0 0 1 0 10h-8M7 9h.01M10 14v6h10v-6",
  detergent: "M7 8h10l2 3v9a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-9l2-3ZM9 8V5h4v3M13 5h3l1.5 3M9 14h6",
  spray: "M8 10h7l1 11H7l1-11ZM9 10V6h5v4M9 6h7l2 2M18.5 5h.01M20.5 7h.01M20.5 3h.01",
  soap: "M8 9h8v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9ZM10 9V6h4v3M12 6V3h3M10 14h4",
  trashBag: "M12 7c-4 0-7 4.5-7 9 0 3 2.5 5 7 5s7-2 7-5c0-4.5-3-9-7-9ZM12 7l-2-3.5M12 7l2-3.5M10 3.5h4",
  sponge: "M3 10l3-3h15v7l-3 3H3v-7ZM3 10h15v7M18 10l3-3M7.5 13h.01M11 14h.01M14 12.5h.01",
  battery: "M4 8h14v8H4zM18 10.5h2v3h-2M7 12h4M9 10v4",
  bulb: "M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1v2h5v-2c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3Z",
  foil: "M3 9h18v7H3zM3 9l2-3h14l2 3M6 12.5h12",
  // toiletries
  toothbrush: "M4 20L14.5 9.5M14.5 9.5l2 2 4.5-4.5-2-2-4.5 4.5ZM16 6l-1.5-1.5M18 4l-1.5-1.5",
  toothpaste: "M4 14l9-9 6 6-9 9H6l-2-2v-4ZM13 5l2-2 6 6-2 2M7 17l3-3",
  soapBar: "M4 11c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3v4c0 2-1.5 3-3.5 3h-9C5.5 18 4 17 4 15v-4ZM8 5.5a1 1 0 1 0 2 0 1 1 0 1 0-2 0ZM13 4a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0Z",
  deodorant: "M8 8h8v13H8zM9 8V5a3 3 0 0 1 6 0v3M8 12h8",
  tissues: "M4 11h16v9H4zM9 11c0-1 .5-1.5 1-2 .8-.8.5-2.5 2-4 .7 1.5 3 2 3 4 0 1-.5 1.5-1 2M8 15h8",
  razor: "M5 4h14v4H5zM12 8v13M8 6h8",
  tube: "M7 3h10l-1 13H8L7 3ZM9.5 16v3h5v-3M10 8h4M12 6v4",
  // baby
  diaper: "M3 7h18v3c0 5-4 9-9 9s-9-4-9-9V7ZM3 10h4a5 5 0 0 0 10 0h4",
  wipes: "M3 8c0-1 1-2 2-2h14c1 0 2 1 2 2v9c0 1-1 2-2 2H5c-1 0-2-1-2-2V8ZM9 10h6a1.5 1.5 0 0 1 0 3H9a1.5 1.5 0 0 1 0-3Z",
  babyBottle: "M8 10h8v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10ZM7.5 8h9v2h-9zM10 8c0-2 .5-4.5 2-5.5 1.5 1 2 3.5 2 5.5M8 14h3M8 17h3",
  // pet
  paw: "M12 20c-2.5 0-5-1-5-3.5S9.5 12 12 12s5 2 5 4.5-2.5 3.5-5 3.5ZM4.2 11a1.8 2.2 0 1 0 3.6 0 1.8 2.2 0 1 0-3.6 0ZM7.7 6.5a1.8 2.2 0 1 0 3.6 0 1.8 2.2 0 1 0-3.6 0ZM12.7 6.5a1.8 2.2 0 1 0 3.6 0 1.8 2.2 0 1 0-3.6 0ZM16.2 11a1.8 2.2 0 1 0 3.6 0 1.8 2.2 0 1 0-3.6 0Z",
  bone: "M9 7.5l7.5 7.5M7.5 9l7.5 7.5M3.5 7a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM5 5.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM15 18.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0ZM16.5 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0Z",
  bowl: "M3 13h18l-2 6H5l-2-6ZM7 13c0-2 2-3.5 5-3.5s5 1.5 5 3.5M10 8h.01M13 7h.01M11.5 6h.01",
} as const;

export type FoodGlyphName = keyof typeof FOOD;

export const isFoodGlyph = (v: string): v is FoodGlyphName => Object.prototype.hasOwnProperty.call(FOOD, v);

/** A glyph's path data (an unknown key draws a shopping bag), for drawing inside another SVG. */
export const foodPath = (name: string): string => FOOD[isFoodGlyph(name) ? name : "bag"];

export function FoodGlyph({ name, size = "1.6rem", className }: { name: string; size?: number | string; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={foodPath(name)} />
    </svg>
  );
}

/**
 * The "tonight" illustration: a plate with cutlery and a wisp of steam,
 * drawn from theme tokens so it suits dark and light. Purely decorative.
 */
export function PlateArt({ empty = false, size = "5rem" }: { empty?: boolean; size?: string }) {
  return (
    <svg className={`plate-art ${empty ? "is-empty" : ""}`} width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <radialGradient id="km-plate-face" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="var(--plate-hi)" />
          <stop offset="100%" stopColor="var(--plate)" />
        </radialGradient>
        <radialGradient id="km-plate-food" cx="45%" cy="40%" r="65%">
          <stop offset="0%" stopColor="var(--accent-2)" />
          <stop offset="100%" stopColor="var(--accent)" />
        </radialGradient>
      </defs>
      {/* fork */}
      <path className="plate-cutlery" d="M14 22v18a6 6 0 0 0 12 0V22M20 22v16M20 46v52" />
      {/* knife */}
      <path className="plate-cutlery" d="M104 98V22c-7 5-10 15-10 32h10" />
      {/* plate */}
      <circle cx="60" cy="68" r="32" fill="url(#km-plate-face)" className="plate-rim" />
      <circle cx="60" cy="68" r="22" fill="none" className="plate-well" />
      {!empty && (
        <g className="plate-food">
          <ellipse cx="60" cy="69" rx="15" ry="11" fill="url(#km-plate-food)" />
          <circle cx="53" cy="64" r="3.2" className="plate-herb" />
          <circle cx="64" cy="62" r="2.4" className="plate-herb" />
          <circle cx="67" cy="72" r="2.8" className="plate-herb" />
        </g>
      )}
      {!empty && (
        <g className="plate-steam">
          <path d="M50 30c-4-5 4-8 0-13" />
          <path d="M60 27c-4-5 4-8 0-13" />
          <path d="M70 30c-4-5 4-8 0-13" />
        </g>
      )}
    </svg>
  );
}
